import { showToast } from '@lokalise/louis';
import type { SagaIterator } from 'redux-saga';
import { call, put, select, takeEvery } from 'redux-saga/effects';

import completeDownloadOnboarding from '@app/features/quick-start/utils/completeDownloadOnboarding';
import track, { AnalyticsEventName } from '@app/utils/analytics';

import type { PostDownloadErrorResponse, PostDownloadFileResponse, PostDownloadResponse } from './api';
import { isPostDownloadErrorResponse, postDownload } from './api';
import { isCustomFileAndFolderStructureVisible } from './components/DownloadPage/utils/visibilityHelpers';
import { downloadDataActions } from './reducers/downloadData/reducers';
import { getFileFormatMapping, getProjectLanguages, getProjectType } from './reducers/downloadData/selectors';
import { downloadExportActions } from './reducers/downloadExport/reducers';
import { downloadGenerateActions } from './reducers/downloadGenerate/reducers';
import type { DebugDownloadWindow } from './types';
import { FileFormat } from './types';
import prepareExportData from './utils/prepareExportData';

const getError = (data: PostDownloadFileResponse | PostDownloadErrorResponse): string | null => {
    if (isPostDownloadErrorResponse(data)) {
        return data.response.message;
    }
    if (data.err === '1') {
        return 'No keys to export with selected options. You may want to include all platforms with the export.';
    }

    return null;
};

const onError = (exception: { response: { data: { error: { message: string } } } }) => {
    const message = exception?.response?.data?.error?.message;

    showToast({
        title: 'Something went wrong!',
        description: message ?? 'Please refresh the page and try again.',
        type: 'error',
        autoClose: message === undefined ? undefined : false,
    });
};

const getFileStructure = (isCustomFileAndFolderStructure: boolean, filenamesOriginal: boolean) => {
    if (!isCustomFileAndFolderStructure) {
        return null;
    }

    return filenamesOriginal ? 'previously_assigned_filename' : 'new_bundle';
};

function* download({ payload }: ReturnType<typeof downloadExportActions.download>): SagaIterator {
    try {
        const { data }: PostDownloadResponse = yield call(postDownload, prepareExportData(payload));

        const error = getError(data);

        if (error) {
            yield put(downloadExportActions.setError(error));
        }

        if (!isPostDownloadErrorResponse(data)) {
            window.location.assign(data.file);

            const {
                values: { fileFormat, filenamesOriginal, exportLangs },
            } = payload;

            // "yield select()" returns all state that makes it possible to select values from it in a typesafe way. https://github.com/redux-saga/redux-saga/issues/970#issuecomment-447292418
            const state = yield select();
            const projectType = getProjectType(state);
            const languages = getProjectLanguages(state);
            const fileFormatMapping = getFileFormatMapping(state);

            track(AnalyticsEventName.FILE_DOWNLOADED, {
                file_format: fileFormatMapping?.[fileFormat],
                file_structure: getFileStructure(
                    isCustomFileAndFolderStructureVisible(fileFormat, projectType),
                    filenamesOriginal,
                ),
                languages: languages
                    .filter(({ id }) => exportLangs.includes(id))
                    .map(({ langIso }) => langIso)
                    .join(', '),
            });

            completeDownloadOnboarding(false);
        }
    } catch (e) {
        onError(e as never);
    } finally {
        yield put(downloadExportActions.loaded());
        yield put(downloadDataActions.updateExportConfig(payload.values));
    }
}

const fileFormatAndMobileOperatingSystemMap: Partial<Record<FileFormat, string>> = {
    [FileFormat.iosSdk]: 'ota-bundles/ios',
    [FileFormat.androidSdk]: 'ota-bundles/android',
    [FileFormat.flutterSdk]: 'ota-bundles/flutter',
};

function* generate({ payload }: ReturnType<typeof downloadExportActions.download>) {
    try {
        const { data }: PostDownloadResponse = yield postDownload(prepareExportData(payload));

        const error = getError(data);

        if (error) {
            yield put(downloadExportActions.setError(error));
        }

        if (!isPostDownloadErrorResponse(data)) {
            const {
                projectId,
                values: { fileFormat },
            } = payload;

            if ([FileFormat.iosSdk, FileFormat.androidSdk, FileFormat.flutterSdk].includes(fileFormat)) {
                completeDownloadOnboarding(true);

                window.location.assign(`/settings/${projectId}/?#${fileFormatAndMobileOperatingSystemMap[fileFormat]}`);

                return;
            }
            yield put(downloadGenerateActions.setFile(data.file));
        }
    } catch (e) {
        onError(e as never);
    } finally {
        yield put(downloadExportActions.loaded());
        yield put(downloadDataActions.updateExportConfig(payload.values));
    }
}

function* downloadAfterGenerate({
    payload,
}: ReturnType<typeof downloadExportActions.downloadAfterGenerate>): SagaIterator {
    const {
        values: { fileFormat, filenamesOriginal, exportLangs },
    } = payload;

    try {
        const state = yield select();
        const projectType = getProjectType(state);
        const languages = getProjectLanguages(state);
        const fileFormatMapping = getFileFormatMapping(state);

        track(AnalyticsEventName.FILE_DOWNLOADED, {
            file_format: fileFormatMapping?.[fileFormat],
            file_structure: getFileStructure(
                isCustomFileAndFolderStructureVisible(fileFormat, projectType),
                filenamesOriginal,
            ),
            languages: languages
                .filter(({ id }) => exportLangs.includes(id))
                .map(({ langIso }) => langIso)
                .join(', '),
        });
    } catch (e) {
        onError(e as never);
    } finally {
        yield put(downloadExportActions.loaded());
    }
}

function preview({ payload }: ReturnType<typeof downloadExportActions.preview>) {
    try {
        const data = prepareExportData(payload);

        // services/app/legacy/views/download/download-preview.twig:1
        if (window.debugDownload && !window.debugDownload.closed) {
            window.debugDownload.debugDownloadData = data;
            window.debugDownload.renderFiles();
        } else {
            window.debugDownloadData = data;
            window.debugDownload = window.open('/download/download-preview', 'debugDownload') as DebugDownloadWindow;
        }
    } catch (e) {
        onError(e as never);
    }
}

export default function* initDownloadSagas() {
    yield takeEvery(downloadExportActions.download.match, download);
    yield takeEvery(downloadExportActions.generate.match, generate);
    yield takeEvery(downloadExportActions.downloadAfterGenerate.match, downloadAfterGenerate);
    yield takeEvery(downloadExportActions.preview.match, preview);
}
